import { z } from 'zod';
import { FlowRunType } from './runType.js';
import { FlowRunStartDataSchema } from './startData.js';
import { FlowReRunVersionTarget, FlowTriggerVersionTarget } from '../flow/versionTarget.js';
/**
 * Path parameters `POST /execute/{flowId}` accepts. The id is not validated as a uuid, which the
 * endpoint predates; a malformed one fails the lookup instead.
 */
export const FlowExecuteParamsSchema = z.object({
    flowId: z.string().describe('The flow to start a run of.'),
});
/** Path parameters `POST /scenario-trigger/{flowId}` accepts. */
export const FlowScenarioTriggerParamsSchema = z.object({
    flowId: z.uuid().describe('The flow to start a run of.'),
});
/**
 * What the new run is about: who it is for and what kind of run it is.
 *
 * `POST /execute/{flowId}` takes these as query parameters and the scenario trigger takes them in
 * its body, so they are declared once and spread into both.
 */
const FlowExecuteSubjectShape = {
    practiceId: z.string().describe('Practice the run belongs to.').optional(),
    entityId: z.uuid().describe('Entity, such as a patient, the run is about.').optional(),
    entityType: z.string().describe('Type of the entity the run is about.').optional(),
    type: z
        .enum(FlowRunType)
        .describe('Kind of run to record. A run of an unpublished definition is always a Test.')
        .optional(),
};
export const FlowExecuteQuerySchema = z.object(FlowExecuteSubjectShape);
/** Path parameters `POST /execute-definition/{flowDefinitionId}` accepts. */
export const FlowExecuteDefinitionParamsSchema = z.object({
    flowDefinitionId: z.string().describe('The exact flow definition to start a run of.'),
});
/**
 * Body `POST /execute-definition/{flowDefinitionId}` accepts. Naming the definition leaves nothing
 * to select, so this carries no version target.
 */
export const FlowExecuteDefinitionBodySchema = z.object({
    blockMocks: z
        .record(z.string(), z.unknown())
        .describe('Output to substitute for a block, keyed by block name.')
        .optional(),
    startData: FlowRunStartDataSchema.describe('Payload the run starts with.').optional(),
});
/**
 * Body `POST /execute/{flowId}` accepts.
 *
 * `versionTarget` decides which definition of the flow runs; `allowUnpublishedFlowRun` is the older
 * way of saying the same thing and is read only when `versionTarget` is absent.
 */
export const FlowExecuteBodySchema = FlowExecuteDefinitionBodySchema.extend({
    /** @deprecated Pass `versionTarget` instead. */
    allowUnpublishedFlowRun: z.boolean().optional().default(true),
    versionTarget: z
        .enum(FlowTriggerVersionTarget)
        .describe('Which definition of the flow to run, such as the published one.')
        .optional(),
});
/** Query parameters `POST /re-run/{flowRunId}` accepts. */
export const FlowReRunQuerySchema = z.object({
    versionTarget: z
        .enum(FlowReRunVersionTarget)
        .describe("Which definition to re-run against: the published one or the original run's.")
        .optional()
        .default(FlowReRunVersionTarget.Published),
});
/** Body `POST /re-run/{flowRunId}` accepts. Omit `startData` to reuse the original run's. */
export const FlowReRunBodySchema = z.object({
    startData: FlowRunStartDataSchema.describe('Payload to start the new run with.').optional(),
});
/**
 * Body `POST /resume/{flowRunId}` accepts.
 *
 * A resume starts a new run that skips ahead to `blockId`, carrying the finished run's block output
 * with it, so the block named here is where execution picks up rather than where it stopped.
 */
export const FlowResumeBodySchema = z.object({
    versionTarget: z
        .enum(FlowReRunVersionTarget)
        .describe("Which definition to resume against: the published one or the original run's.")
        .default(FlowReRunVersionTarget.Published),
    blockId: z.uuid().describe('Block to resume execution from.'),
});
/** One run in a bulk request, naming either the flow or the exact definition to run. */
export const FlowExecuteBulkRunSchema = z.object({
    flowDefinitionId: z.string().describe('The exact definition to run.').optional(),
    flowId: z.string().describe('The flow to run, when no definition is named.').optional(),
    entityType: z.string().optional(),
    entityId: z.uuid().optional(),
    practiceId: z.string().optional(),
    blockMocks: z
        .record(z.string(), z.unknown())
        .describe('Output to substitute for a block, keyed by block name.')
        .optional(),
    testCaseId: z.string().describe('Test case this run carries out.').optional(),
});
/**
 * Body `POST /execute-bulk` accepts.
 *
 * The runs are started as one named group, which is how they are found and cancelled afterwards,
 * and `concurrentLimit` is what keeps a large group from starting all at once.
 */
export const FlowExecuteBulkBodySchema = z.object({
    runGroupName: z.string().describe('Name the group of runs is recorded under.'),
    organizationId: z.uuid().describe('Organization the runs belong to.'),
    type: z.enum(FlowRunType).describe('Kind of run to record, such as Test.').optional(),
    flowRuns: z.array(FlowExecuteBulkRunSchema).describe('The runs to start.'),
    concurrentLimit: z
        .number()
        .min(1)
        .max(100)
        .describe('How many of the runs may be in flight at once.')
        .optional()
        .default(10),
    useExperimentalTaskQueue: z.boolean().optional().default(false),
});
/**
 * Body `POST /scenario-trigger/{flowId}` accepts.
 *
 * `ifNotStartedSince` is a deduplication guard: the run is skipped when the entity already started
 * one after that time, which is why it needs `entityType` and `entityId` alongside it. flow-api
 * checks that pairing, since it is a rule about the request rather than a shape.
 */
export const FlowScenarioTriggerBodySchema = z.object({
    ...FlowExecuteSubjectShape,
    startData: FlowRunStartDataSchema.describe('Payload the run starts with.').optional(),
    ifNotStartedSince: z
        .string()
        .describe('Skip the run when this entity already started one at or after this time.')
        .optional()
        .nullable(),
    /** @deprecated Pass `versionTarget` instead. */
    allowUnpublishedFlowRun: z.boolean().optional().default(true),
    versionTarget: z
        .enum(FlowTriggerVersionTarget)
        .describe('Which definition of the flow to run, such as the published one.')
        .optional(),
    triggeringScenarioIds: z
        .array(z.string())
        .describe('Scenarios that asked for this run.')
        .optional(),
    feedIds: z.array(z.string()).optional(),
    maxFlowRunsPerMinute: z.coerce
        .number()
        .positive()
        .describe('Throughput ceiling for the fleet that carries out the run.')
        .optional(),
    executeWithFleets: z.boolean().optional().default(false),
});
//# sourceMappingURL=execute.js.map